import { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2, Clock, Truck, User, Eye, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriverSchedule = () => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [activeTab, setActiveTab] = useState('created');
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [driverFilter, setDriverFilter] = useState('all');
  const [formData, setFormData] = useState({
    driver_id: '',
    schedule_date: '',
    start_time: '',
    finish_time: '',
    vehicle_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [schedulesRes, driversRes, vehiclesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/driver-schedules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/employees/department/drivers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/vehicles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const schedulesData = await schedulesRes.json();
      const driversData = await driversRes.json();
      const vehiclesData = await vehiclesRes.json();

      setSchedules(schedulesData);
      setDrivers(driversData);
      setVehicles(vehiclesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.driver_id || !formData.schedule_date || !formData.start_time || !formData.finish_time || !formData.vehicle_id) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingSchedule 
        ? `${BACKEND_URL}/api/driver-schedules/${editingSchedule.id}`
        : `${BACKEND_URL}/api/driver-schedules`;
      
      const response = await fetch(url, {
        method: editingSchedule ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to save schedule');
      }

      toast.success(editingSchedule ? 'Schedule updated successfully' : 'Schedule created successfully');
      setShowDialog(false);
      setEditingSchedule(null);
      setFormData({
        driver_id: '',
        schedule_date: '',
        start_time: '',
        finish_time: '',
        vehicle_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save schedule');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      driver_id: schedule.driver_id,
      schedule_date: schedule.schedule_date,
      start_time: schedule.start_time,
      finish_time: schedule.finish_time,
      vehicle_id: schedule.vehicle_id
    });
    setShowDialog(true);
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/driver-schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete schedule');
      }

      toast.success('Schedule deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const openAddDialog = () => {
    setEditingSchedule(null);
    setFormData({
      driver_id: '',
      schedule_date: '',
      start_time: '',
      finish_time: '',
      vehicle_id: ''
    });
    setShowDialog(true);
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : 'Unknown';
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.registration} (${vehicle.vehicle_type})` : 'Unknown';
  };

  const getFilteredSchedules = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return schedules.filter(schedule => {
      // Filter by status/tab
      const status = schedule.status || 'created';
      let passesTabFilter = false;
      if (activeTab === 'created') passesTabFilter = status === 'created';
      else if (activeTab === 'live') passesTabFilter = status === 'live';
      else if (activeTab === 'completed') passesTabFilter = status === 'completed';
      else passesTabFilter = true;
      
      if (!passesTabFilter) return false;
      
      // Filter by driver
      if (driverFilter !== 'all' && schedule.driver_id !== driverFilter) {
        return false;
      }
      
      // Filter by date
      const scheduleDate = new Date(schedule.schedule_date);
      scheduleDate.setHours(0, 0, 0, 0);
      
      let passesDateFilter = true;
      
      if (dateFilter === 'today') {
        passesDateFilter = scheduleDate.getTime() === today.getTime();
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        passesDateFilter = scheduleDate.getTime() === yesterday.getTime();
      } else if (dateFilter === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        passesDateFilter = scheduleDate.getTime() === tomorrow.getTime();
      } else if (dateFilter === 'week') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Sunday
        passesDateFilter = scheduleDate >= weekStart && scheduleDate <= today;
      } else if (dateFilter === 'month') {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        passesDateFilter = scheduleDate >= monthStart && scheduleDate <= today;
      } else if (dateFilter === 'year') {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        passesDateFilter = scheduleDate >= yearStart && scheduleDate <= today;
      } else if (dateFilter === 'custom') {
        // Custom date range - from start to end
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          passesDateFilter = scheduleDate >= startDate && scheduleDate <= endDate;
        } else if (customStartDate) {
          const startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          passesDateFilter = scheduleDate >= startDate;
        } else if (customEndDate) {
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          passesDateFilter = scheduleDate <= endDate;
        }
      }
      
      return passesDateFilter;
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const isLate = (actualTime, scheduledTime) => {
    if (!actualTime || !scheduledTime) return false;
    const actual = new Date(`2000-01-01T${actualTime.includes('T') ? actualTime.split('T')[1] : actualTime}`);
    const scheduled = new Date(`2000-01-01T${scheduledTime}`);
    return actual > scheduled;
  };

  const handleViewLog = (schedule) => {
    setSelectedLog(schedule);
    setShowLogDialog(true);
  };

  const filteredSchedules = getFilteredSchedules();

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="driver-schedule-container">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Driver Schedules</h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage driver schedules</p>
          </div>
          <Button
            onClick={openAddDialog}
            className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
            data-testid="create-schedule-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Schedule
          </Button>
        </div>
      </div>

      {/* Tab Navigation - Desktop */}
      <div className="mb-6 hidden md:block">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('created')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'created'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-created"
            >
              Created
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                activeTab === 'created' ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {schedules.filter(s => (s.status || 'created') === 'created').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'live'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-live"
            >
              Live
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                activeTab === 'live' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {schedules.filter(s => s.status === 'live').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'completed'
                  ? 'border-gray-500 text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-completed"
            >
              Completed
              <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                activeTab === 'completed' ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {schedules.filter(s => s.status === 'completed').length}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Navigation - Mobile Dropdown */}
      <div className="mb-6 md:hidden">
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="created">Created ({schedules.filter(s => (s.status || 'created') === 'created').length})</option>
          <option value="live">Live ({schedules.filter(s => s.status === 'live').length})</option>
          <option value="completed">Completed ({schedules.filter(s => s.status === 'completed').length})</option>
        </select>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className={`grid gap-4 ${dateFilter === 'custom' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {/* Driver Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driver</label>
            <select
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Drivers</option>
              {drivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.first_name && driver.last_name 
                    ? `${driver.first_name} ${driver.last_name}` 
                    : driver.name || driver.email}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="week">Week to Date</option>
              <option value="month">Month to Date</option>
              <option value="year">Year to Date</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {/* Custom Date Range Pickers */}
          {dateFilter === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Schedules List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading schedules...</p>
        </div>
      ) : filteredSchedules.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">
            {schedules.length === 0 ? 'No schedules created yet' : `No ${activeTab} schedules`}
          </p>
          {schedules.length === 0 && (
            <Button onClick={openAddDialog} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-2" />
              Create First Schedule
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Driver
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Start Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Finish Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSchedules.map((schedule) => (
                    <tr key={schedule.id} className="hover:bg-gray-50" data-testid={`schedule-row-${schedule.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{schedule.driver_name || getDriverName(schedule.driver_id)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{new Date(schedule.schedule_date).toLocaleDateString('en-GB')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">Expected: {schedule.start_time}</span>
                          </div>
                          {schedule.clock_on_time ? (
                            <span className={`text-sm font-semibold ${isLate(schedule.clock_on_time, schedule.start_time) ? 'text-red-600' : 'text-green-600'}`}>
                              Actual: {formatTime(schedule.clock_on_time)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Not clocked in</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">Expected: {schedule.finish_time}</span>
                          </div>
                          {schedule.clock_off_time ? (
                            <span className={`text-sm font-semibold ${isLate(schedule.clock_off_time, schedule.finish_time) ? 'text-red-600' : 'text-green-600'}`}>
                              Actual: {formatTime(schedule.clock_off_time)}
                            </span>
                          ) : schedule.clock_on_time ? (
                            <span className="text-sm font-semibold text-blue-600">In progress</span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{schedule.vehicle_registration || getVehicleInfo(schedule.vehicle_id)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          schedule.status === 'live' ? 'bg-green-100 text-green-800' :
                          schedule.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {schedule.status === 'created' ? 'Ready' : schedule.status === 'live' ? 'In Progress' : 'Completed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex gap-2 justify-end">
                          {schedule.log_data && (
                            <Button
                              onClick={() => handleViewLog(schedule)}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid={`view-log-${schedule.id}`}
                            >
                              View Log
                            </Button>
                          )}
                          <Button
                            onClick={() => handleEdit(schedule)}
                            size="sm"
                            variant="outline"
                            data-testid={`edit-schedule-${schedule.id}`}
                            disabled={schedule.status !== 'created'}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(schedule.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-600 hover:bg-red-50"
                          data-testid={`delete-schedule-${schedule.id}`}
                          disabled={schedule.status !== 'created'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {filteredSchedules.map((schedule) => (
            <div
              key={schedule.id}
              className="bg-white rounded-xl shadow-md p-4"
              data-testid={`schedule-card-${schedule.id}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-teal-600" />
                  <span className="font-bold text-gray-900">
                    {schedule.driver_name || getDriverName(schedule.driver_id)}
                  </span>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  schedule.status === 'live' ? 'bg-green-100 text-green-800' :
                  schedule.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {schedule.status === 'created' ? 'Ready' : schedule.status === 'live' ? 'In Progress' : 'Completed'}
                </span>
              </div>

              {/* Date & Vehicle */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{new Date(schedule.schedule_date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{schedule.vehicle_registration || getVehicleInfo(schedule.vehicle_id)}</span>
                </div>
              </div>

              {/* Time Details */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Start Time */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Start Time</div>
                  <div className="flex items-center gap-1 mb-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">Expected: {schedule.start_time}</span>
                  </div>
                  {schedule.clock_on_time ? (
                    <span className={`text-sm font-semibold ${isLate(schedule.clock_on_time, schedule.start_time) ? 'text-red-600' : 'text-green-600'}`}>
                      Actual: {formatTime(schedule.clock_on_time)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Not clocked in</span>
                  )}
                </div>

                {/* Finish Time */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Finish Time</div>
                  <div className="flex items-center gap-1 mb-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">Expected: {schedule.finish_time}</span>
                  </div>
                  {schedule.clock_off_time ? (
                    <span className={`text-sm font-semibold ${isLate(schedule.clock_off_time, schedule.finish_time) ? 'text-red-600' : 'text-green-600'}`}>
                      Actual: {formatTime(schedule.clock_off_time)}
                    </span>
                  ) : schedule.clock_on_time ? (
                    <span className="text-sm font-semibold text-blue-600">In progress</span>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                {schedule.log_data && (
                  <Button
                    onClick={() => handleViewLog(schedule)}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                    data-testid={`view-log-mobile-${schedule.id}`}
                  >
                    View Log
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(schedule)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    data-testid={`edit-schedule-mobile-${schedule.id}`}
                    disabled={schedule.status !== 'created'}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(schedule.id)}
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50 flex-1"
                    data-testid={`delete-schedule-mobile-${schedule.id}`}
                    disabled={schedule.status !== 'created'}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="driver_id">Driver</Label>
              <select
                id="driver_id"
                value={formData.driver_id}
                onChange={(e) => setFormData({...formData, driver_id: e.target.value})}
                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="driver-select"
              >
                <option value="">Select a driver</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.first_name} {driver.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="schedule_date">Date</Label>
              <Input
                id="schedule_date"
                type="date"
                value={formData.schedule_date}
                onChange={(e) => setFormData({...formData, schedule_date: e.target.value})}
                className="mt-1"
                data-testid="schedule-date-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  className="mt-1"
                  data-testid="start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="finish_time">Finish Time</Label>
                <Input
                  id="finish_time"
                  type="time"
                  value={formData.finish_time}
                  onChange={(e) => setFormData({...formData, finish_time: e.target.value})}
                  className="mt-1"
                  data-testid="finish-time-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="vehicle_id">Vehicle</Label>
              <select
                id="vehicle_id"
                value={formData.vehicle_id}
                onChange={(e) => setFormData({...formData, vehicle_id: e.target.value})}
                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="vehicle-select"
              >
                <option value="">Select a vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration} ({vehicle.vehicle_type})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => {
                setShowDialog(false);
                setEditingSchedule(null);
                setFormData({
                  driver_id: '',
                  schedule_date: '',
                  start_time: '',
                  finish_time: '',
                  vehicle_id: ''
                });
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="submit-schedule-button"
            >
              {editingSchedule ? 'Update' : 'Create'} Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Log Dialog */}
      <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Driver Log Details</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Schedule Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Schedule Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Driver:</span>
                    <span className="ml-2 font-medium">{selectedLog.driver_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <span className="ml-2 font-medium">{new Date(selectedLog.schedule_date).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Scheduled:</span>
                    <span className="ml-2 font-medium">{selectedLog.start_time} - {selectedLog.finish_time}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Vehicle:</span>
                    <span className="ml-2 font-medium">{selectedLog.vehicle_registration}</span>
                  </div>
                </div>
              </div>

              {/* Clock Times */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Clock Times</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Clock On:</span>
                    <div className="mt-1">
                      <span className={`font-medium ${isLate(selectedLog.clock_on_time, selectedLog.start_time) ? 'text-red-600' : 'text-green-600'}`}>
                        {formatTime(selectedLog.clock_on_time)}
                      </span>
                      {isLate(selectedLog.clock_on_time, selectedLog.start_time) && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">⚠ LATE</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Clock Off:</span>
                    <div className="mt-1">
                      {selectedLog.clock_off_time ? (
                        <>
                          <span className={`font-medium ${isLate(selectedLog.clock_off_time, selectedLog.finish_time) ? 'text-orange-600' : 'text-green-600'}`}>
                            {formatTime(selectedLog.clock_off_time)}
                          </span>
                          {isLate(selectedLog.clock_off_time, selectedLog.finish_time) && (
                            <span className="ml-2 text-xs text-orange-600 font-semibold">⏰ OVERTIME</span>
                          )}
                        </>
                      ) : (
                        <span className="text-blue-600 font-medium">In Progress</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Log Details */}
              {selectedLog.log_data && (
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Journey Details</h3>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-gray-600">Start Mileage:</span>
                        <span className="ml-2 font-medium">{selectedLog.log_data.start_mileage || 'Not recorded'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">End Mileage:</span>
                        <span className="ml-2 font-medium">{selectedLog.log_data.end_mileage || 'Not recorded'}</span>
                      </div>
                    </div>
                    
                    {selectedLog.log_data.start_mileage && selectedLog.log_data.end_mileage && (
                      <div>
                        <span className="text-gray-600">Distance Covered:</span>
                        <span className="ml-2 font-medium text-teal-600">
                          {selectedLog.log_data.end_mileage - selectedLog.log_data.start_mileage} miles
                        </span>
                      </div>
                    )}

                    {selectedLog.log_data.notes && (
                      <div>
                        <span className="text-gray-600 block mb-1">Notes:</span>
                        <div className="bg-gray-50 p-2 rounded text-gray-900">
                          {selectedLog.log_data.notes}
                        </div>
                      </div>
                    )}

                    {selectedLog.log_data.had_accident && (
                      <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                        <span className="text-red-800 font-semibold">⚠️ ACCIDENT REPORTED</span>
                      </div>
                    )}

                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className={`ml-2 font-medium ${selectedLog.log_data.status === 'submitted' ? 'text-green-600' : 'text-orange-600'}`}>
                        {selectedLog.log_data.status === 'submitted' ? 'Submitted' : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowLogDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverSchedule;
